import i18n from '../i18n';

// Auth token getter, registered by AuthProvider on session change
let authTokenGetter: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  authTokenGetter = getter;
}

/**
 * Get current Supabase access token.
 * Used by services that need direct token access (e.g., XMLHttpRequest for file uploads).
 */
export async function getAuthToken(): Promise<string | null> {
  if (!authTokenGetter) {
    console.error('❌ getAuthToken called before AuthProvider initialized');
    return null;
  }
  return authTokenGetter();
}

// API Configuration
const API_BASE_URL = process.env['REACT_APP_API_URL'] || 'http://localhost:8000/api';
const API_TIMEOUT = 60000; // 60 seconds (increased for complex queries)

// Custom error class
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
    public url?: string
  ) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

// API Client class
class ApiClient {
  private baseURL: string;
  private timeout: number;
  private inFlightRequests: Map<string, Promise<any>> = new Map();

  constructor(baseURL: string = API_BASE_URL, timeout: number = API_TIMEOUT) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  // Main request method
  async request<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const method = options.method || 'GET';
    const dedupeKey = `${method}:${url}`;

    // Deduplicate GET requests only. The key has no body, so deduplicating
    // mutations would silently drop the second caller's payload and hand it
    // the first request's response.
    const shouldDeduplicate = method === 'GET';

    // Reuse an in-flight identical GET instead of firing a second request
    if (shouldDeduplicate && this.inFlightRequests.has(dedupeKey)) {
      return this.inFlightRequests.get(dedupeKey)!;
    }

    // Abort the request if it exceeds the timeout
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), this.timeout);

    // Default headers
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Inject auth token (set by AuthProvider via setAuthTokenGetter)
    if (authTokenGetter) {
      try {
        const token = await authTokenGetter();
        if (token) {
          defaultHeaders['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    } else {
      console.error('❌ API Request BLOCKED - authTokenGetter is null! Endpoint:', endpoint);
      throw new Error(i18n.t('toast.authNotInitialized', { ns: 'errors' }));
    }

    // Merge options
    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: abortController.signal,
    };

    const requestPromise = (async () => {
      try {
        const response = await fetch(url, config);

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new ApiError(
            response.status,
            response.statusText,
            await response.json().catch(() => null),
            url
          );
        }

        // Parse response
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        } else {
          return await response.text() as T;
        }
      } catch (error) {
        clearTimeout(timeoutId);

        // Surface a timeout abort as a clear error
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error(i18n.t('toast.cancelled', { ns: 'errors' }));
        }

        throw error;
      } finally {
        if (shouldDeduplicate) {
          this.inFlightRequests.delete(dedupeKey);
        }
      }
    })();

    if (shouldDeduplicate) {
      this.inFlightRequests.set(dedupeKey, requestPromise);
    }

    return requestPromise;
  }

  // Convenience methods
  async get<T = unknown>(endpoint: string, options?: RequestInit) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = unknown>(endpoint: string, data?: unknown, options?: RequestInit) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = unknown>(endpoint: string, data?: unknown, options?: RequestInit) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T = unknown>(endpoint: string, data?: unknown, options?: RequestInit) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = unknown>(endpoint: string, options?: RequestInit) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Create default instance
export const apiClient = new ApiClient();
